import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { ACTIVE_ORG_STORAGE_KEY } from '../utils/orgRoutes';

export interface SwitchOrgTarget {
  id: string;
  display_name: string;
}

/**
 * Hook returning a function that switches the active organization via the
 * back-channel ``refresh_token`` grant -- the same flow the CLI uses (see
 * ``packages/cli/src/utils/auth.rs::switch_org``).
 *
 * The browser-redirect flow routes through Auth0's silent-SSO path, which
 * sometimes drops the ``organization`` parameter and returns a token for the
 * previous org. The refresh-token grant has no SSO cookie at the token
 * endpoint, so Auth0 either honors the org or returns a 4xx -- no silent
 * failure mode.
 *
 * On success the page reloads so every ``useOrgClaims`` instance and all
 * org-scoped data refetch against the new token; ``OrgUrlSync`` runs its
 * verification effect on mount and surfaces the success / mismatch toast. The
 * ``antioch_org_switch_target*`` sessionStorage markers drive that effect.
 *
 * :return: a function resolving to ``true`` when the token request succeeded
 *     (a reload follows) and ``false`` when it failed (caller should restore
 *     the current-org URL)
 */
export function useSwitchOrg(): (target: SwitchOrgTarget) => Promise<boolean> {
  const { getAccessTokenSilently } = useAuth0();

  return useCallback(
    async (target: SwitchOrgTarget) => {
      // Markers consumed by OrgUrlSync's verification effect post-reload. The
      // reload below preserves the URL, so no return-path key is needed.
      sessionStorage.setItem('antioch_org_switch_target', target.display_name);
      sessionStorage.setItem('antioch_org_switch_target_id', target.id);

      try {
        // cacheMode: 'off' forces a network call; with useRefreshTokens enabled
        // on the provider, the SDK uses the `refresh_token` grant carrying the
        // organization parameter -- equivalent to the CLI's POST /oauth/token
        await getAccessTokenSilently({
          authorizationParams: { organization: target.id },
          cacheMode: 'off',
        });
      } catch (err) {
        sessionStorage.removeItem('antioch_org_switch_target');
        sessionStorage.removeItem('antioch_org_switch_target_id');
        const message = err instanceof Error ? err.message : 'Unknown error';
        toast.error(`Failed to switch to ${target.display_name}: ${message}`);
        return false;
      }

      // Persist the target org BEFORE reloading so AuthProvider pins the new
      // org on bootstrap. useOrgClaims only writes this key after mount (i.e.
      // after the reload); without this the provider would re-pin the previous
      // org and the next silent refresh would drift the session back (ENG-1217
      // review).
      try {
        window.localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, target.id);
      } catch {
        // localStorage unavailable -- non-fatal; useOrgClaims re-persists post-reload.
      }

      window.location.reload();
      return true;
    },
    [getAccessTokenSilently]
  );
}
